import { errorMessage } from '@/api/client'
import { Card } from '@/components/ui'

/**
 * The presentational pieces the three P2 screens share.
 *
 * They live under `Case/` because Case is where the run vocabulary is defined
 * and the other two screens are windows onto it — New case starts a run, Queue
 * lists what runs concluded, and both have to report a failed read in the same
 * words the run screen does.
 *
 * `RunErrorCard` is a deliberate copy of Admin's `ErrorCard` rather than an
 * import of it. Importing a screen module from `screens/Admin` to get a card
 * would pull the admin data layer — LLM configs, service keys, the API-key
 * mint — into every product bundle that renders a docket, and would make
 * "who owns this component" ambiguous between two agents' directories. Twenty
 * lines is the cheaper of the two costs.
 */

/**
 * A read that failed.
 *
 * `errorMessage` is what the codebase puts in front of a person, so a 403 reads
 * as a refusal and a network drop reads as a network drop — neither becomes
 * "something went wrong", which sends somebody looking for a bug that is not
 * there.
 */
export function RunErrorCard({
  title,
  error,
  flush = false,
}: {
  title: string
  error: unknown
  flush?: boolean
}) {
  const body = (
    <>
      <h5 className="m-0">{title}</h5>
      <p className="m-0 text-body text-muted">{errorMessage(error)}</p>
    </>
  )
  if (flush) {
    return (
      <div role="alert" className="flex flex-col gap-1">
        {body}
      </div>
    )
  }
  return (
    <Card role="alert" className="border-block-line bg-block-bg">
      {body}
    </Card>
  )
}

/** The muted one-liner every card uses while its query is in flight. */
export function LoadingLine({ label }: { label: string }) {
  return <p className="m-0 text-body text-muted">{label}</p>
}
